/**
 * Drives Cart Checkout in two stages, mirroring `useCreatePurchase.ts`'s own
 * two-step shape:
 *
 *   1. `runCheckout` — validates + re-prices the whole cart server-side and
 *      fans it out into N independent Purchases (09_CART_DOMAIN.md §4). The
 *      cart is CONVERTED the instant this succeeds — it cannot be checked
 *      out again, so a retry after this step must never call it again.
 *   2. Sequentially drives each resulting Purchase through the EXISTING,
 *      unmodified per-Purchase Checkout endpoint (`createCheckout` from
 *      `modules/purchase/api.ts`) to get a Razorpay order per item.
 *
 * `payForOrder(purchaseId)` — opens the Razorpay Checkout.js widget for one
 * checked-out order, reusing the exact same `openRazorpayCheckout` module
 * `CheckoutPage.tsx`'s single "Buy Now" flow uses. Each order is an
 * independent Purchase, so payment state is tracked per-`purchaseId` in
 * `payments`, never as one combined phase — a customer can pay for order 1,
 * have it fail, and still successfully pay for order 2 without either
 * affecting the other. A Razorpay success callback is never trusted
 * directly (see `razorpayCheckout.ts`'s module doc and
 * `purchaseOutcome.ts`) — it polls `getPurchase` for the backend-verified
 * outcome, exactly like the single-item flow.
 *
 * A failure partway through step 2 is resumable: `retryOrderCreation`
 * continues from the first Purchase without an order yet, never re-calling
 * `runCheckout` (the cart is already CONVERTED) and never re-creating an
 * order for a Purchase that already has one (Milestone 3's `createCheckout`
 * is itself idempotent-by-Invoice, but skipping already-done items avoids
 * the redundant round-trip).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { checkoutCart, CartCheckoutRejectedError, CartRejectedError, CartStateChangedError } from './api';
import { createCheckout, retryCheckout, listPurchases } from '../purchase/api';
import { openRazorpayCheckout, CheckoutUnavailableError, type RazorpayFailurePayload } from '../purchase/razorpayCheckout';
import { relayCheckoutResult } from '../purchase/relayCheckoutResult';
import { pollPurchaseUntilResolved, fetchPurchaseOutcomeOnce, PAYMENT_FAILED_MESSAGE, PAYMENT_UNAVAILABLE_MESSAGE } from '../purchase/pollPurchaseOutcome';
import type { CartCheckoutFailure, CartPurchase } from './types';
import type { CreateCheckoutResponse } from '../purchase/types';

const CHECKOUT_FAILED_MESSAGE = 'We couldn’t start checkout. Please try again.';
const ORDER_CREATION_FAILED_MESSAGE =
  'Your order was created, but we couldn’t start payment for one item. Please try again — it won’t be duplicated.';
const NO_CHECKOUT_TO_PAY_MESSAGE = 'We couldn’t start payment. Please try again.';

const PENDING_ORDERS_STORAGE_KEY = 'cartCheckout:pendingOrders';

interface PersistedPendingOrders {
  purchases: CartPurchase[];
  checkoutDetails: CreateCheckoutResponse[];
}

function mintIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `retry-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readPendingOrders(): PersistedPendingOrders | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ORDERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as PersistedPendingOrders).purchases) ||
      !Array.isArray((parsed as PersistedPendingOrders).checkoutDetails)
    ) {
      return null;
    }
    return parsed as PersistedPendingOrders;
  } catch {
    // Corrupted entry, or storage unavailable — fall back to a normal cold start, never crash.
    return null;
  }
}

function writePendingOrders(purchases: CartPurchase[], checkoutDetails: CreateCheckoutResponse[]): void {
  try {
    const data = JSON.stringify({ purchases, checkoutDetails });
    sessionStorage.setItem(PENDING_ORDERS_STORAGE_KEY, data);
  } catch {
    // Best-effort — losing reload-recovery is acceptable, failing the
    // checkout flow over it is not.
  }
}

function clearPendingOrders(): void {
  try {
    sessionStorage.removeItem(PENDING_ORDERS_STORAGE_KEY);
  } catch {
    // Best-effort, same reasoning as writePendingOrders.
  }
}



export type CartCheckoutPhase = 'idle' | 'checking-out' | 'creating-orders' | 'ready-for-payment' | 'error';

export type CartOrderPaymentPhase =
  | 'idle'
  | 'awaiting-gateway'
  | 'confirming'
  | 'confirmation-pending'
  | 'paid'
  | 'payment-failed';

export interface CartOrderPaymentState {
  phase: CartOrderPaymentPhase;
  error: string | null;
}

export interface UseCartCheckoutResult {
  phase: CartCheckoutPhase;
  /** How many of the checked-out Purchases already have a Razorpay order. */
  progress: { total: number; completed: number };
  purchases: CartPurchase[];
  checkoutDetails: CreateCheckoutResponse[];
  error: string | null;
  /** Per-line rejection reasons — set only when the WHOLE checkout was rejected before any Purchase was created. */
  failures: CartCheckoutFailure[];
  /** Per-order payment state, keyed by `purchaseId`. Absent until `payForOrder` is first called for that order. */
  payments: Record<string, CartOrderPaymentState>;
  runCheckout: () => Promise<void>;
  retryOrderCreation: () => Promise<void>;
  /** Opens the Razorpay widget for one order's already-issued checkout detail. */
  payForOrder: (purchaseId: string) => Promise<void>;
  /** One manual re-check from `confirmation-pending` for a single order. */
  refreshOrderStatus: (purchaseId: string) => Promise<void>;
  reset: () => void;
}

export function useCartCheckout(): UseCartCheckoutResult {
  // Once (mount only, real lazy-init semantics via useState's initializer
  // form): restores a checked-out-but-unpaid cart across a reload. Without
  // this, `runCheckout` CONVERTS the cart the instant it succeeds — the N
  // Purchases + gateway orders it produced then existed only in React
  // state, so a reload (or closing the tab) after paying 1 of 3 stranded
  // the remaining, still-payable orders until they expired server-side
  // (code review finding — a real, customer-facing money-recovery gap).
  const [initialPending] = useState(() => readPendingOrders());

  const [phase, setPhase] = useState<CartCheckoutPhase>(initialPending ? 'ready-for-payment' : 'idle');
  const [purchases, setPurchases] = useState<CartPurchase[]>(initialPending?.purchases ?? []);
  const [checkoutDetails, setCheckoutDetails] = useState<CreateCheckoutResponse[]>(initialPending?.checkoutDetails ?? []);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<CartCheckoutFailure[]>([]);
  const [payments, setPayments] = useState<Record<string, CartOrderPaymentState>>({});

  // Keyed by purchaseId — lets a newer payForOrder call for the SAME order
  // (e.g. retry after dismiss) supersede an in-flight poll for that order,
  // without affecting any other order's independent poll. Mirrors
  // `useCreatePurchase.ts`'s single global `attemptIdRef`, scoped per-order
  // here because Cart has N independent payable orders at once.
  const attemptIdsRef = useRef<Record<string, number>>({});

  const reset = useCallback(() => {
    setPhase('idle');
    setPurchases([]);
    setCheckoutDetails([]);
    setError(null);
    setFailures([]);
    setPayments({});
    // Without this, a stale in-flight poll from before reset() still passes
    // its `isCurrent` check (attemptIdsRef itself wasn't touched) and writes
    // a resolved outcome back into the freshly-cleared state. Caught in
    // code review.
    attemptIdsRef.current = {};
    clearPendingOrders();
  }, []);

  const setOrderPayment = useCallback((purchaseId: string, next: CartOrderPaymentState) => {
    setPayments((prev) => ({ ...prev, [purchaseId]: next }));
  }, []);

  /**
   * Only applies `next` if this order's phase is still one of
   * `allowedPhases` — a later event (payment.failed, a success->confirming
   * transition) may have already moved it on. Reads the LATEST state via
   * the functional updater, not a closed-over `payments` value, so this
   * stays correct even though `payForOrder` isn't re-created every render.
   * Fixes a real bug (code review): Razorpay's `modal.ondismiss` fires even
   * after `payment.failed` or a success `handler` callback (the user still
   * has to close the widget), and unconditionally resetting to `idle` wiped
   * out the decline message / reverted an already-confirming order.
   */
  const setOrderPaymentIfPhase = useCallback(
    (purchaseId: string, allowedPhases: CartOrderPaymentPhase[], next: CartOrderPaymentState) => {
      setPayments((prev) => {
        const current = prev[purchaseId]?.phase ?? 'idle';
        if (!allowedPhases.includes(current)) return prev;
        return { ...prev, [purchaseId]: next };
      });
    },
    [],
  );

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isCurrent = useCallback((purchaseId: string, attemptId: number) => {
    return mountedRef.current && attemptIdsRef.current[purchaseId] === attemptId;
  }, []);

  const pollOrderUntilResolved = useCallback(
    async (purchaseId: string, attemptId: number) => {
      const result = await pollPurchaseUntilResolved(purchaseId, () => isCurrent(purchaseId, attemptId));
      if (!result) return; // superseded or unmounted
      if (result.kind === 'confirmation-pending') {
        setOrderPayment(purchaseId, { phase: 'confirmation-pending', error: null });
        return;
      }
      setOrderPayment(purchaseId, { phase: result.kind, error: result.kind === 'payment-failed' ? PAYMENT_FAILED_MESSAGE : null });
    },
    [isCurrent, setOrderPayment],
  );

  const payForOrder = useCallback(
    async (purchaseId: string) => {
      let detail = checkoutDetails.find((d) => d.purchaseId === purchaseId);
      if (!detail) {
        const target = purchases.find((p) => p.id === purchaseId);
        if (target) {
          try {
            setOrderPayment(purchaseId, { phase: 'awaiting-gateway', error: null });
            detail = target.status === 'PAYMENT_PENDING'
              ? await retryCheckout(target.id, mintIdempotencyKey())
              : await createCheckout(target.id);
            setCheckoutDetails((prev) => {
              const next = [...prev.filter((d) => d.purchaseId !== purchaseId), detail!];
              writePendingOrders(purchases, next);
              return next;
            });
          } catch {
            setOrderPayment(purchaseId, { phase: 'payment-failed', error: NO_CHECKOUT_TO_PAY_MESSAGE });
            return;
          }
        } else {
          setOrderPayment(purchaseId, { phase: 'payment-failed', error: NO_CHECKOUT_TO_PAY_MESSAGE });
          return;
        }
      }
      const attemptId = (attemptIdsRef.current[purchaseId] ?? 0) + 1;
      attemptIdsRef.current[purchaseId] = attemptId;
      setOrderPayment(purchaseId, { phase: 'awaiting-gateway', error: null });

      try {
        await openRazorpayCheckout({
          keyId: detail.checkout.keyId,
          // Saved cards on the cart path too, so the buyer gets the same Razorpay
          // experience whichever flow they came through. Null-safe (option omitted).
          customerId: detail.checkout.customerId,
          gatewayOrderId: detail.checkout.gatewayOrderId,
          amountPaise: detail.checkout.amountPaise,
          currency: detail.checkout.currency,
          onSuccess: (response) => {
            if (!isCurrent(purchaseId, attemptId)) return;
            setOrderPayment(purchaseId, { phase: 'confirming', error: null });
            // Advisory relay for server-side HMAC verification — never
            // drives this order's phase; only the poll below does.
            void relayCheckoutResult(purchaseId, response);
            void pollOrderUntilResolved(purchaseId, attemptId);
          },
          onDismiss: () => {
            if (!isCurrent(purchaseId, attemptId)) return;
            // The gateway order was never consumed — the SAME checkout
            // detail can be reopened without a new createCheckout call.
            // Only applies if nothing else has happened since the widget
            // opened (see setOrderPaymentIfPhase's doc) — the widget's own
            // modal.ondismiss still fires after payment.failed or a success
            // handler, and must not erase either outcome.
            setOrderPaymentIfPhase(purchaseId, ['awaiting-gateway'], { phase: 'idle', error: null });
          },
          onFailure: (_failure: RazorpayFailurePayload) => {
            if (!isCurrent(purchaseId, attemptId)) return;
            setOrderPayment(purchaseId, { phase: 'payment-failed', error: PAYMENT_FAILED_MESSAGE });
          },
        });
      } catch (err) {
        // Checkout.js itself failed to load (ad-blocker, offline, CSP).
        if (!isCurrent(purchaseId, attemptId)) return;
        setOrderPayment(purchaseId, { phase: 'payment-failed', error: err instanceof CheckoutUnavailableError ? PAYMENT_UNAVAILABLE_MESSAGE : PAYMENT_FAILED_MESSAGE });
      }
    },
    [checkoutDetails, isCurrent, pollOrderUntilResolved, purchases, setOrderPayment, setOrderPaymentIfPhase],
  );

  const refreshOrderStatus = useCallback(
    async (purchaseId: string) => {
      // Snapshot by identity, not `?? 0` — a purchaseId that never went
      // through `payForOrder` has no entry at all (`undefined`), and
      // defaulting that to `0` made the post-fetch guard below always fail
      // for it (`undefined === 0` is false), silently discarding the fetch.
      // Caught in code review.
      const attemptIdAtStart = attemptIdsRef.current[purchaseId];
      try {
        const result = await fetchPurchaseOutcomeOnce(purchaseId);
        if (!mountedRef.current || attemptIdsRef.current[purchaseId] !== attemptIdAtStart) return;
        if (result) {
          setOrderPayment(purchaseId, { phase: result.kind, error: result.kind === 'payment-failed' ? PAYMENT_FAILED_MESSAGE : null });
        }
        // `result === null` means still pending — same convention as
        // `useCreatePurchase.ts`'s own `refreshStatus`: leave the current
        // state as-is rather than guess an outcome.
      } catch {
        // Best-effort — stay on confirmation-pending, the user can try again.
      }
    },
    [setOrderPayment],
  );

  // Runs once, only when this render started from a rehydrated session
  // (`initialPending` was read at mount, see its declaration above) — syncs
  // each restored order's LOCAL `payments` phase to the real backend status
  // immediately, rather than trusting stale local state (which isn't
  // persisted at all — only the durable `purchases`/`checkoutDetails` are).
  useEffect(() => {
    if (!initialPending) return;
    for (const detail of initialPending.checkoutDetails) {
      void refreshOrderStatus(detail.purchaseId);
    }
    // Deliberately NOT depending on `initialPending` (it's stable for the
    // lifetime of this hook instance, captured once via useState) — only on
    // `refreshOrderStatus`, itself stable, so this runs exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOrderStatus]);

  // Rehydrate open/pending cart purchases on mount from server across reloads.
  // Scoped strictly to non-pack cart purchases that have not expired.
  useEffect(() => {
    let cancelled = false;
    listPurchases()
      .then((res) => {
        if (cancelled) return;
        const now = Date.now();
        const pendingPurchases = res.items.filter((p) => {
          if (p.status !== 'PAYMENT_PENDING' && p.status !== 'CHECKOUT_CREATED') return false;
          // Omit credit packs — those belong in Settings → Billing
          if (p.isCreditPack || p.creditPackId || p.collectionCode?.startsWith('PARTNER_')) return false;
          // Omit if past expiration
          const exp = p.expiresAt;
          if (exp && new Date(exp).getTime() <= now) return false;
          return true;
        });
        if (pendingPurchases.length === 0) return;

        setPurchases((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const toAdd: CartPurchase[] = [];
          for (const p of pendingPurchases) {
            if (!existingIds.has(p.id)) {
              toAdd.push({
                id: p.id,
                userId: '',
                invitationId: null,
                collectionCode: p.collectionCode,
                status: p.status,
                amount: p.amount,
                expiresAt: p.expiresAt ?? '',
                createdAt: p.createdAt,
                version: 1,
              });
            }
          }
          if (toAdd.length === 0 && prev.length > 0) return prev;
          const merged = [...prev, ...toAdd];
          writePendingOrders(merged, []);
          return merged;
        });

        setPhase((prevPhase) => (prevPhase === 'idle' || prevPhase === 'error' ? 'ready-for-payment' : prevPhase));
      })
      .catch(() => {
        // Best-effort
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const createOrdersFrom = useCallback(async (targetPurchases: CartPurchase[], alreadyDone: CreateCheckoutResponse[]) => {
    setPhase('creating-orders');
    const details = [...alreadyDone];
    for (const purchase of targetPurchases.slice(alreadyDone.length)) {
      try {
        const detail = purchase.status === 'PAYMENT_PENDING'
          ? await retryCheckout(purchase.id, mintIdempotencyKey())
          : await createCheckout(purchase.id);
        details.push(detail);
        setCheckoutDetails([...details]);
        writePendingOrders(targetPurchases, details);
      } catch {
        setPhase('error');
        setError(ORDER_CREATION_FAILED_MESSAGE);
        return;
      }
    }
    setPhase('ready-for-payment');
    // Persist once every order has a gateway order — see this hook's module
    // doc and `readPendingOrders`'s comment for why (reload-recovery).
    writePendingOrders(targetPurchases, details);
  }, []);

  const runCheckout = useCallback(
    async () => {
      setError(null);
      setFailures([]);
      setPhase('checking-out');
      let result;
      try {
        result = await checkoutCart();
      } catch (err) {
        setPhase('error');
        if (err instanceof CartCheckoutRejectedError) {
          setFailures(err.failures);
          setError(err.message);
        } else if (err instanceof CartStateChangedError || err instanceof CartRejectedError) {
          // CartRejectedError carries a specific, actionable message for
          // known codes (CART_TOO_LARGE_FOR_CHECKOUT, CART_EMPTY) — caught
          // in code review: this used to fall through to the generic
          // "please retry" message even for failures a retry can never fix.
          setError(err.message);
        } else {
          setError(CHECKOUT_FAILED_MESSAGE);
        }
        return;
      }
      setPurchases(result.purchases);
      await createOrdersFrom(result.purchases, []);
    },
    [createOrdersFrom],
  );

  const retryOrderCreation = useCallback(async () => {
    await createOrdersFrom(purchases, checkoutDetails);
  }, [purchases, checkoutDetails, createOrdersFrom]);

  return {
    phase,
    progress: { total: purchases.length, completed: checkoutDetails.length },
    purchases,
    checkoutDetails,
    error,
    failures,
    payments,
    runCheckout,
    retryOrderCreation,
    payForOrder,
    refreshOrderStatus,
    reset,
  };
}
